import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useEntityRegistry } from "@/features/backoffice";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageLayout } from "../layout";

/**
 * The catalogue of everything the back-office can show.
 *
 * The sidebar pins the five entities support opens every day; this page is the
 * other thirty-five. It is generated from the server's registry, so a table
 * added there appears here without a frontend change.
 */
export function AdminExplorerIndexPage() {
  const { data, isLoading } = useEntityRegistry();
  const [filter, setFilter] = useState("");

  const grouped = useMemo(() => {
    if (!data) return [];
    const needle = filter.trim().toLowerCase();

    return data.groups
      .map((group) => ({
        ...group,
        entities: data.entities.filter(
          (entity) =>
            entity.group === group.id &&
            (!needle ||
              entity.label.toLowerCase().includes(needle) ||
              entity.table.toLowerCase().includes(needle) ||
              entity.description?.toLowerCase().includes(needle)),
        ),
      }))
      .filter((group) => group.entities.length > 0);
  }, [data, filter]);

  return (
    <AdminPageLayout
      title="Explorateur de données"
      description="Toutes les tables de la plateforme, tous tenants confondus. Lecture seule."
      actions={
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filtrer les entités…"
            className="h-9 w-64 pl-9"
          />
        </div>
      }
    >
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 9 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          Aucune entité ne correspond.
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((group) => (
            <section key={group.id} className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </h3>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {group.entities.map((entity) => (
                  <Link
                    key={entity.table}
                    to={`/admin/explorer/${entity.table}`}
                  >
                    <Card className="h-full transition-colors hover:border-primary/50 hover:bg-muted/40">
                      <CardContent className="p-4 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{entity.label}</span>
                          {entity.searchable && (
                            <Badge variant="secondary" className="text-[10px]">
                              recherchable
                            </Badge>
                          )}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground">
                          {entity.table}
                        </div>
                        {entity.description && (
                          <p className="pt-1 text-xs text-muted-foreground">
                            {entity.description}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </AdminPageLayout>
  );
}
